import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, type PanInfo } from "framer-motion";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { heroSlides } from "../../data/site";
import { IntroSlide } from "./hero/IntroSlide";
import { ProductSlide } from "./hero/ProductSlide";
const ease = [0.23, 1, 0.32, 1] as const;
const SLIDE_MS = 8000;
const count = heroSlides.length + 1;
export function Hero() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(true);
  const sectionRef = useRef<HTMLElement>(null);
  const go = useCallback((dir: number) => setIndex(i => (i + dir + count) % count), []);

  // Only auto-advance while the header is on screen. Below desktop the header's height follows the
  // active slide, so changing slides off screen would shift the page under the reader.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.25
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (paused || !inView) return;
    const t = window.setTimeout(() => go(1), SLIDE_MS);
    return () => window.clearTimeout(t);
  }, [index, paused, inView, go]);
  const onPanEnd = (_e: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => {
    if (info.offset.x < -60) go(1);else if (info.offset.x > 60) go(-1);
  };
  const slides = [{
    id: "intro",
    render: (active: boolean) => <IntroSlide active={active} data-id="element-354" data-mp="34pzwfklfnfrs" />
  }, ...heroSlides.map(s => ({
    id: s.id,
    render: (active: boolean) => <ProductSlide slide={s} active={active} data-id="element-355" data-mp="2vro6lkse4m0e" />
  }))];
  return <section ref={sectionRef} id="top" aria-roledescription="carousel" aria-label="Highlights"
  // Desktop: exactly one screen tall. Phones & tablets: as tall as the active slide, so slide 1 isn't
  // stretched to the height of the taller product slides.
  className="relative flex flex-col overflow-hidden bg-sun pt-24 md:pt-28 lg:h-[100svh] lg:min-h-[680px]" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} data-id="element-356" data-mp="2vrxyjdwzx38q">
      {/* min-h-0 lets this column shrink to the screen height so the arrow bar is never pushed out of view. */}
      <div className="relative mx-auto flex w-full max-w-[1400px] flex-1 flex-col px-5 md:px-8 lg:min-h-0" data-id="element-357" data-mp="1b8c1gsoccp27">
        <motion.div className="relative grid flex-1 lg:min-h-0 lg:grid-rows-[minmax(0,1fr)]" onPanEnd={onPanEnd} style={{
        touchAction: "pan-y"
      }} data-id="element-358" data-mp="16b2ctf8s0hff">
          {slides.map((s, i) => {
          const isActive = i === index;
          return <motion.div key={s.id} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${count}`} aria-hidden={!isActive}
          // Below desktop, hidden slides are lifted out of the layout so only the active one sets the height.
          className={`col-start-1 row-start-1 lg:min-h-0 lg:overflow-hidden ${isActive ? "z-10" : "pointer-events-none absolute inset-x-0 top-0 lg:static"}`} initial={false} animate={{
            opacity: isActive ? 1 : 0
          }} transition={{
            duration: 0.3,
            ease
          }} data-id="element-359" data-mp="1tzv3aw6mpx2n" data-mpi={i}>
                {s.render(isActive)}
              </motion.div>;
        })}
        </motion.div>

        <div className="relative z-10 flex shrink-0 items-center justify-end gap-3 border-t-2 border-ink py-4" data-id="element-360" data-mp="2p9i2e9a8zij4">
          <button type="button" onClick={() => go(-1)} aria-label="Previous slide" className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-ink transition-colors duration-150 hover:bg-ink hover:text-sun" data-id="element-361" data-mp="2u8kzn7jzkkgj">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-362" data-mp="4rykt8h45wye" />
          </button>
          <div className="flex items-center gap-2 px-1" data-id="element-363" data-mp="4q5drs2l9apv">
            {slides.map((s, i) => <button key={s.id} type="button" onClick={() => setIndex(i)} aria-label={`Go to slide ${i + 1}`} aria-current={i === index ? "true" : undefined} className="py-3" data-id="element-364" data-mp="3vwyq9s2u0w9p" data-mpi={i}>
                <span className="block h-1.5 w-10 overflow-hidden rounded-full bg-ink/20 md:w-14" data-id="element-365" data-mp="3i14f12el5utf">
                  {i === index && (paused ? <span className="block h-full w-full rounded-full bg-ink" data-id="element-366" data-mp="3r0hqu1q6mvhb" /> : <motion.span key={index} className="block h-full rounded-full bg-ink" initial={{
                width: "0%"
              }} animate={{
                width: "100%"
              }} transition={{
                duration: SLIDE_MS / 1000,
                ease: "linear"
              }} data-id="element-367" data-mp="3lz24m6c9qixc" />)}
                </span>
              </button>)}
          </div>
          <span className="w-12 text-center text-[12px] font-bold tabular-nums" data-id="element-368" data-mp="zt7cky70z5eh">
            {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
          </span>
          <button type="button" onClick={() => go(1)} aria-label="Next slide" className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-ink transition-colors duration-150 hover:bg-ink hover:text-sun" data-id="element-369" data-mp="14uelx1otgcn9">
            <ArrowRightIcon className="h-5 w-5" data-id="element-370" data-mp="1irtdod2jkwie" />
          </button>
        </div>
      </div>
    </section>;
}